// BrokerCRM.js — vFinal+++++++++++++++++ (CRM-first, login-aware branch routing + shared nav)
// --------------------------------------------------------------------------------------------------
// ✅ Broker CRM opens directly to Mobile
// ✅ MobileStep stays source of truth for Mercury lookup results
// ✅ FIX: Removed mobile-step snapshot rehydrate that could overwrite found opp/contact IDs
// ✅ Sidebar shows ONLY "Mobile" until mobile is entered, then reveals all tabs
// ✅ After mobile is entered: sidebar navigation remains FREE to jump
// ✅ Keeps autosave, final submit, notes, validation, dialler, documents, snapshots
// ✅ Reactive URL branch sync for SPA URL changes
// ✅ Passes branchContext prop into MobileStep
// ✅ screenPopToMobile preserves branch params + Mercury IDs + crmRef in URL
// ✅ FIX: Preserves full virtual-branch routing context: branchKey / virtualBranch / tokenKey / branchScope
// ✅ FIX: Backfills branch params into browser URL as soon as branch context is known
// ✅ NEW: Falls back to logged-in broker profile from localStorage (mmBrokerProfile)
// ✅ NEW: Shared Back / Next nav for all middle steps so buttons always match
// ✅ NEW: Expenses step now gets the same nav as other steps
// ✅ FIX: Prevents duplicate Applicant 2 payload when Applicant 1 and 2 are the same person
// ✅ FIX: Income step now passes the real shared updater to IncomeExpenses

import React, { useState, useCallback, useEffect, useRef, useMemo } from "react"

// TOP NAV
import CRMTopNav from "./CRMTopNav"

// STEP COMPONENTS
import MobileStep from "./steps/MobileStep"
import PropertyStep from "./steps/PropertyStep"
import ApplicantStep from "./steps/ApplicantStep"
import IncomeExpenses from "./steps/IncomeExpenses"
import AssetsLiabilities from "./steps/AssetsLiabilities"
import ExpensesStep from "./steps/ExpensesStep"
import NeedsObjectives from "./steps/NeedsObjectives"
import BorrowingCapacityStep from "./steps/BorrowingCapacityStep"
import ReviewSubmit from "./steps/ReviewSubmit"
import SuggestedQuestions from "./steps/SuggestedQuestions"
import DialerPanel from "./steps/DialerPanel"
import DocumentsTab from "./steps/DocumentsTab"

import styles from "./BrokerCRM.module.css"

// URLs
const SAVE_URL =
  "https://65u162eij0.execute-api.ap-southeast-2.amazonaws.com/default/MortgageMatrixBrokerCRMSaveToMercury"

const SEND_URL =
  "https://duh7zoxbvd.execute-api.ap-southeast-2.amazonaws.com/default/MortgageMatrixSendToMercury"

const APPEND_URL = "https://hgm3oocdseth67gbhdlqca5iru0hegsh.lambda-url.ap-southeast-2.on.aws/"

const SNAPSHOT_UPSERT_URL =
  "https://4px4lwzqhxzpea5iarjbhzlwu0zcgmr.lambda-url.ap-southeast-2.on.aws/"

const SNAPSHOT_GET_BY_MOBILE_URL =
  "https://op5zzga2sqqpkdjmftcznhz75i0hxkxk.lambda-url.ap-southeast-2.on.aws/"

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------
const digitsOnly = (v) => String(v ?? "").replace(/[^\d]/g, "")

const toNumberSafe = (v) => {
  const n = Number(String(v ?? "").replace(/[^\d.]/g, ""))
  return Number.isFinite(n) ? n : 0
}

const ownerToApplicantIndex = (owner) => {
  const s = String(owner || "").toLowerCase()
  if (s.includes("2")) return 2
  return 1
}

const normaliseAddr = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/,/g, "")
    .trim()

const toYMD = (v) => {
  if (!v) return ""
  const s = String(v).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s

  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (m) {
    const dd = m[1].padStart(2, "0")
    const mm = m[2].padStart(2, "0")
    const yyyy = m[3]
    return `${yyyy}-${mm}-${dd}`
  }

  const d = new Date(s)
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10)

  return ""
}

function safeJsonParse(s) {
  try {
    return JSON.parse(s)
  } catch {
    return { raw: s }
  }
}

function parseLambdaResponse(raw) {
  const statusCode = Number(raw?.statusCode) || Number(raw?.StatusCode) || undefined

  let parsedBody = raw
  if (raw && typeof raw === "object" && typeof raw.body === "string") {
    parsedBody = safeJsonParse(raw.body)
  }

  return { statusCode, parsedBody }
}

async function fetchJsonLoose(url, options) {
  let r
  try {
    r = await fetch(url, options)
  } catch (e) {
    const msg = e?.message || "Network error"
    return { ok: false, status: 0, statusCode: 0, parsed: { success: false, message: msg }, rawText: "" }
  }

  let raw
  let rawText = ""
  try {
    raw = await r.json()
  } catch {
    rawText = await r.text().catch(() => "")
    raw = { success: false, message: rawText || "Non-JSON response" }
  }

  const { statusCode, parsedBody } = parseLambdaResponse(raw)

  return {
    ok: r.ok,
    status: r.status,
    statusCode,
    parsed: parsedBody,
    rawText,
  }
}

const pickPrimaryEmploymentRow = (rowsForApplicant = []) => {
  const employmentRows = rowsForApplicant.filter((r) => String(r?.incomeType || "") === "Employment")
  if (employmentRows.length) {
    return employmentRows
      .slice()
      .sort((a, b) => Number(digitsOnly(b?.amount)) - Number(digitsOnly(a?.amount)))[0]
  }
  return rowsForApplicant
    .slice()
    .sort((a, b) => Number(digitsOnly(b?.amount)) - Number(digitsOnly(a?.amount)))[0]
}

const inferEmploymentTypeForMercury = (row) => {
  const explicit = String(row?.employmentType || "").trim()
  if (explicit) return explicit

  if (String(row?.incomeType || "") === "Employment") return "Payg"

  const blob = `${row?.employerName || ""} ${row?.roleDescription || ""} ${row?.category || ""}`.toLowerCase()

  const selfHints = [
    "self",
    "sole trader",
    "soletrader",
    "contractor",
    "subcontract",
    "abn",
    "pty ltd",
    "pty. ltd",
    "business",
    "company",
    "director",
    "trust",
  ]

  if (selfHints.some((h) => blob.includes(h))) return "Self Employment"
  return "Payg"
}

const mapIncomeRowToApplicantEmploymentFields = (row) => {
  const amountDigits = digitsOnly(row?.amount)
  const amount = amountDigits ? Number(amountDigits) : 0

  return {
    employer: row?.employerName || "",
    occupation: row?.roleDescription || "",
    startDate: toYMD(row?.startDate || ""),
    income: amount > 0 ? amount : 0,

    employmentType: inferEmploymentTypeForMercury(row),
    employmentBasis: row?.employmentBasis || "",
    paygEmployerType: row?.paygEmployerType || "",

    abn: row?.abn || "",
    natureOfBusiness: row?.natureOfBusiness || "",
    operatingStructure: row?.operatingStructure || "",
  }
}

const cleanStr = (v) => String(v || "").trim()
const cleanEmail = (v) => cleanStr(v).toLowerCase()
const cleanPhone = (v) => String(v || "").replace(/[^\d]/g, "")

const applicantsAreSamePerson = (a1 = {}, a2 = {}) => {
  const a1Id = cleanStr(a1.contactId || a1.id)
  const a2Id = cleanStr(a2.contactId || a2.id)
  if (a1Id && a2Id && a1Id === a2Id) return true

  const a1Email = cleanEmail(a1.email)
  const a2Email = cleanEmail(a2.email)
  if (a1Email && a2Email && a1Email === a2Email) return true

  const a1Phone = cleanPhone(a1.mobile || a1.phone)
  const a2Phone = cleanPhone(a2.mobile || a2.phone)
  if (a1Phone && a2Phone && a1Phone === a2Phone && a1Phone.length >= 8) return true

  const a1First = cleanStr(a1.firstName).toLowerCase()
  const a2First = cleanStr(a2.firstName).toLowerCase()
  const a1Last = cleanStr(a1.lastName).toLowerCase()
  const a2Last = cleanStr(a2.lastName).toLowerCase()
  if (a1First && a2First && a1Last && a2Last && a1First === a2First && a1Last === a2Last) return true

  return false
}

const getStoredBrokerProfile = () => {
  try {
    return JSON.parse(localStorage.getItem("mmBrokerProfile") || "{}")
  } catch {
    return {}
  }
}

const pickUrlBranchContext = () => {
  const stored = getStoredBrokerProfile()

  try {
    const params = new URLSearchParams(window.location.search)

    const branchKey =
      params.get("branchKey") ||
      params.get("virtualBranch") ||
      params.get("brokerKey") ||
      params.get("branch") ||
      stored.branchKey ||
      ""

    const virtualBranch =
      params.get("virtualBranch") ||
      stored.virtualBranch ||
      branchKey ||
      ""

    const tokenKey =
      params.get("tokenKey") ||
      params.get("branchTokenKey") ||
      stored.tokenKey ||
      branchKey ||
      ""

    return {
      branchKey,
      virtualBranch,
      tokenKey,
      branchScope:
        params.get("branchScope") ||
        params.get("scope") ||
        stored.branchScope ||
        "broker",
      brokerPersonId:
        params.get("brokerPersonId") ||
        params.get("brokerId") ||
        stored.brokerPersonId ||
        "",
      brokerCA:
        params.get("brokerCA") ||
        params.get("ca") ||
        stored.brokerCA ||
        "",
      branchName:
        params.get("branchName") ||
        params.get("brokerName") ||
        stored.branchName ||
        "",
      role: stored.role || "",
      username: stored.username || "",
    }
  } catch {
    return {
      branchKey: stored.branchKey || "",
      virtualBranch: stored.virtualBranch || "",
      tokenKey: stored.tokenKey || "",
      branchScope: stored.branchScope || "broker",
      brokerPersonId: stored.brokerPersonId || "",
      brokerCA: stored.brokerCA || "",
      branchName: stored.branchName || "",
      role: stored.role || "",
      username: stored.username || "",
    }
  }
}

function installHistoryChangeEvents() {
  if (typeof window === "undefined") return
  if (window.__mm_history_patch_installed__) return
  window.__mm_history_patch_installed__ = true

  const wrapHistoryMethod = (type, eventName) => {
    const original = window.history[type]
    if (typeof original !== "function") return original

    return function wrappedHistoryMethod(...args) {
      const result = original.apply(this, args)
      window.dispatchEvent(new Event(eventName))
      window.dispatchEvent(new Event("locationchange"))
      return result
    }
  }

  window.history.pushState = wrapHistoryMethod("pushState", "pushstate")
  window.history.replaceState = wrapHistoryMethod("replaceState", "replacestate")

  window.addEventListener("popstate", () => {
    window.dispatchEvent(new Event("locationchange"))
  })
}

const applyBranchMeta = (metadata = {}, branch = {}) => ({
  ...(metadata || {}),
  ...(branch?.branchKey ? { branchKey: branch.branchKey } : {}),
  ...(branch?.virtualBranch ? { virtualBranch: branch.virtualBranch } : {}),
  ...(branch?.tokenKey ? { tokenKey: branch.tokenKey } : {}),
  ...(branch?.branchScope ? { branchScope: branch.branchScope } : {}),
  ...(branch?.brokerPersonId ? { brokerPersonId: branch.brokerPersonId } : {}),
  ...(branch?.brokerCA ? { brokerCA: branch.brokerCA } : {}),
  ...(branch?.branchName ? { branchName: branch.branchName } : {}),
  ...(branch?.role ? { role: branch.role } : {}),
  ...(branch?.username ? { username: branch.username } : {}),
})

// ---------------------------------------------------------------------------
// BUILD PAYLOAD FOR MERCURY — FINAL SUBMIT ONLY
// ---------------------------------------------------------------------------
const buildSendPayload = (crm) => {
  const applicants = Array.isArray(crm.applicants) ? crm.applicants : []
  const a1 = applicants[0] || {}
  const rawA2 = applicants[1] || {}

  const applicant2HasMeaningfulData = !!(
    cleanStr(rawA2.firstName) ||
    cleanStr(rawA2.lastName) ||
    cleanStr(rawA2.email) ||
    cleanStr(rawA2.mobile || rawA2.phone)
  )

  const applicant2IsDuplicate = applicantsAreSamePerson(a1, rawA2)
  const hasRealApplicant2 = applicant2HasMeaningfulData && !applicant2IsDuplicate
  const a2 = hasRealApplicant2 ? rawA2 : {}

  const addr = crm.property?.propertyAddress || {}

  const assetsBlock = crm.assetsLiabilities || { assets: [], liabilities: [] }
  const liabsRaw = Array.isArray(assetsBlock?.liabilities) ? assetsBlock.liabilities : []
  const assets = Array.isArray(assetsBlock?.assets) ? assetsBlock.assets : []

  const appType = String(crm.property?.applicationType || "").toLowerCase()
  const isRefi = appType === "refinance"
  const subjectFormatted = crm.property?.propertyAddress?.formatted || ""
  const subjectNorm = normaliseAddr(subjectFormatted)

  const subjectLoanCaptured = (() => {
    if (!isRefi) return false
    const hasBalance = toNumberSafe(crm.property?.loanBalance || 0) > 0
    const hasRate = toNumberSafe(crm.property?.interestRate || 0) > 0
    const hasLender =
      String(crm.property?.lender || crm.property?.creditor || crm.property?.bank || "").trim().length > 0
    return hasBalance || hasRate || hasLender
  })()

  const isSubjectMortgageLiability = (l) => {
    if (!subjectLoanCaptured) return false
    if (String(l?.type || "") !== "Home Loan") return false
    if (String(l?.metaKey || "") === "subject_home_loan") return true
    if (String(l?.propertyKey || "") === "subject") return true

    const ln = normaliseAddr(l?.propertyAddress || "")
    if (ln && subjectNorm && ln === subjectNorm) return true

    return false
  }

  const liabs = liabsRaw.filter((l) => !isSubjectMortgageLiability(l))

  const incomeRows = Array.isArray(crm.income) ? crm.income : []
  const incomeA1 = incomeRows.filter((r) => ownerToApplicantIndex(r?.owner) === 1)
  const incomeA2 = hasRealApplicant2
    ? incomeRows.filter((r) => ownerToApplicantIndex(r?.owner) === 2)
    : []

  const primaryRowA1 = pickPrimaryEmploymentRow(incomeA1)
  const primaryRowA2 = hasRealApplicant2 ? pickPrimaryEmploymentRow(incomeA2) : null

  const empA1 = primaryRowA1 ? mapIncomeRowToApplicantEmploymentFields(primaryRowA1) : {}
  const empA2 = primaryRowA2 ? mapIncomeRowToApplicantEmploymentFields(primaryRowA2) : {}

  const mappedLiabs = liabs.map((l) => ({
    type: l.type || "",
    balance: toNumberSafe(l.balance || 0),
    limit: toNumberSafe(l.limit || 0),
    repayment: toNumberSafe(l.repayment || 0),
    frequency: l.frequency || "monthly",
    institution: l.creditor || "",
    applicant: Number(l.applicant) || 1,
  }))

  const otherAssets = assets
    .filter((a) => {
      if (!String(a?.type || "").trim()) return false
      if (toNumberSafe(a?.value) <= 0) return false
      if (String(a?.metaKey || "") === "deposit_savings") return false
      return true
    })
    .map((a) => ({
      type: a.type || "",
      value: toNumberSafe(a.value || 0),
      details: a.details || "",
      basis: "Applicant",
      ownership: 100,
      applicant: Number(a?.applicant || ownerToApplicantIndex(a?.owner) || 1),
    }))

  return {
    applicant1: {
      firstName: a1.firstName || "",
      lastName: a1.lastName || "",
      email: a1.email || "",
      phone: a1.mobile || a1.phone || "",
      maritalStatus: a1.relationship || a1.relationshipStatus || a1.maritalStatus || "",
      dependants:
        a1.numberOfDependents ??
        a1.numberOfDependants ??
        a1.dependants ??
        0,
      residency: a1.residency || "",
      creditHistory: a1.creditHistory || "",
      dob: a1.dob || a1.dateOfBirth || a1.birthDate || "",

      employer: empA1.employer || "",
      occupation: empA1.occupation || "",
      startDate: empA1.startDate || "",
      income: empA1.income || 0,

      employmentType: empA1.employmentType || "",
      employmentBasis: empA1.employmentBasis || "",
      paygEmployerType: empA1.paygEmployerType || "",

      abn: empA1.abn || "",
      natureOfBusiness: empA1.natureOfBusiness || "",
      operatingStructure: empA1.operatingStructure || "",
    },

    applicant2: hasRealApplicant2
      ? {
          firstName: a2.firstName || "",
          lastName: a2.lastName || "",
          email: a2.email || "",
          phone: a2.mobile || a2.phone || "",
          maritalStatus: a2.relationship || a2.relationshipStatus || a2.maritalStatus || "",
          dependants:
            a2.numberOfDependents ??
            a2.numberOfDependants ??
            a2.dependants ??
            0,
          residency: a2.residency || "",
          creditHistory: a2.creditHistory || "",
          dob: a2.dob || a2.dateOfBirth || a2.birthDate || "",

          employer: empA2.employer || "",
          occupation: empA2.occupation || "",
          startDate: empA2.startDate || "",
          income: empA2.income || 0,

          employmentType: empA2.employmentType || "",
          employmentBasis: empA2.employmentBasis || "",
          paygEmployerType: empA2.paygEmployerType || "",

          abn: empA2.abn || "",
          natureOfBusiness: empA2.natureOfBusiness || "",
          operatingStructure: empA2.operatingStructure || "",
        }
      : null,

    refinancingWithSomeone: hasRealApplicant2,

    propertyAddress: {
      formatted: addr.formatted || "",
      street: addr.street || "",
      suburb: addr.suburb || "",
      state: addr.state || "",
      postcode: addr.postcode || "",
      lat: addr.lat || null,
      lng: addr.lng || null,
    },

    propertyType: crm.property?.propertyType || "",
    rentalIncomeWeekly: crm.property?.rentalIncomeWeekly || 0,

    loanPurpose: crm.property?.applicationType === "purchase" ? "Purchase" : "Refinance",

    propertyValue:
      crm.property?.applicationType === "purchase"
        ? toNumberSafe(crm.property?.purchasePrice || 0)
        : toNumberSafe(crm.property?.propertyValue || 0),

    loanBalance:
      crm.property?.applicationType === "purchase"
        ? 0
        : toNumberSafe(crm.property?.loanBalance || 0),

    interestRate:
      crm.property?.applicationType === "purchase"
        ? 0
        : toNumberSafe(crm.property?.interestRate || 0),

    applicantLiabilities: mappedLiabs.filter((l) => l.applicant === 1),
    coApplicantLiabilities: hasRealApplicant2
      ? mappedLiabs.filter((l) => l.applicant === 2)
      : [],

    otherAssets,

    refinanceGoal: crm.objectives?.refinanceGoal || "",
    leadSource: "Mortgage Matrix Broker CRM",
  }
}

// ---------------------------------------------------------------------------
// MOBILE REQUIRED
// ---------------------------------------------------------------------------
const getMobileFromCrm = (crm) => {
  const m = crm?.mobile || {}
  const raw =
    m.mobile ||
    m.phone ||
    m.mobileNumber ||
    m.phoneNumber ||
    m.number ||
    m.value ||
    crm?.applicants?.[0]?.mobile ||
    ""
  return digitsOnly(raw)
}

const isValidAUNumber = (digits) => {
  if (!digits) return false
  if (digits.length !== 10) return false
  if (!digits.startsWith("0")) return false
  return true
}

// ---------------------------------------------------------------------------
// VALIDATION
// ---------------------------------------------------------------------------
const validateSection = (id, crm) => {
  const mobileDigits = getMobileFromCrm(crm)
  const hasMobile = isValidAUNumber(mobileDigits)

  if (id !== "mobile" && !hasMobile) {
    return { valid: false, message: "Enter a valid mobile number first (this links the file to Mercury)." }
  }

  switch (id) {
    case "mobile": {
      if (!hasMobile) return { valid: false, message: "Please enter a valid Australian mobile number (10 digits)." }
      return { valid: true }
    }

    case "property": {
      if (!crm.property?.applicationType) return { valid: false, message: "Select purchase or refinance." }

      const appType = String(crm.property?.applicationType || "").toLowerCase()
      const found = String(crm.property?.foundProperty || "").toLowerCase()

      const needsAddress = appType === "refinance" || (appType === "purchase" && found === "yes")
      if (needsAddress && !crm.property?.propertyAddress?.formatted) {
        return { valid: false, message: "Enter full property address." }
      }

      return { valid: true }
    }

    case "applicants": {
      const a1 = crm.applicants?.[0]
      if (!a1?.firstName || !a1?.lastName || !a1?.email) {
        return { valid: false, message: "Complete Applicant 1 details." }
      }
      return { valid: true }
    }

    case "income": {
      const rows = Array.isArray(crm.income) ? crm.income : []

      const meaningfulRows = rows.filter((r) => {
        return (
          digitsOnly(r?.amount).length > 0 ||
          String(r?.employerName || "").trim() ||
          String(r?.roleDescription || r?.role || "").trim() ||
          String(r?.startDate || r?.dateCommenced || "").trim() ||
          String(r?.category || "").trim()
        )
      })

      if (!meaningfulRows.length) return { valid: true }

      const hasAnyAmount = meaningfulRows.some((r) => digitsOnly(r?.amount).length > 0)
      if (!hasAnyAmount) return { valid: false, message: "Enter an annual income amount." }

      return { valid: true }
    }

    default:
      return { valid: true }
  }
}

// ---------------------------------------------------------------------------
// ICONS
// ---------------------------------------------------------------------------
const StepIcons = {
  mobile: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="7" y="2.5" width="10" height="19" rx="2" />
      <circle cx="12" cy="18" r="1" />
    </svg>
  ),
  property: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 10.5L12 3l9 7.5" />
      <path d="M5 10v10h14V10" />
    </svg>
  ),
  applicants: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
    </svg>
  ),
  income: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v20" />
      <path d="M17 6H9a4 4 0 000 8h6a4 4 0 010 8H7" />
    </svg>
  ),
  assets: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M16 7V5a4 4 0 00-8 0v2" />
    </svg>
  ),
  expenses: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 2h10l-1 20H8L7 2z" />
      <path d="M9 7h6" />
      <path d="M9 11h6" />
    </svg>
  ),
  objectives: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.5" />
      <path d="M12 3v3" />
      <path d="M21 12h-3" />
      <path d="M12 21v-3" />
      <path d="M3 12h3" />
    </svg>
  ),
  capacity: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19V5" />
      <path d="M20 19V5" />
      <path d="M8 19v-7" />
      <path d="M12 19v-11" />
      <path d="M16 19v-4" />
    </svg>
  ),
  review: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  documents: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H7a2 2 0 00-2 2v16a2 2 0 002 2h10a2 2 0 002-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M9 13h6" />
      <path d="M9 17h6" />
    </svg>
  ),
}

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------
export default function BrokerCRM() {
  const [currentSection, setCurrentSection] = useState("mobile")
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [lastSaved, setLastSaved] = useState(null)

  const [existingMercuryNotes, setExistingMercuryNotes] = useState("")
  const [notesDraft, setNotesDraft] = useState("")
  const [notesLoading, setNotesLoading] = useState(false)
  const [notesError, setNotesError] = useState("")
  const [notesLastSync, setNotesLastSync] = useState(null)

  const [dialerOpen, setDialerOpen] = useState(false)
  const [urlBranchContext, setUrlBranchContext] = useState(() => pickUrlBranchContext())

  useEffect(() => {
    installHistoryChangeEvents()

    const syncBranchContextFromUrl = () => {
      setUrlBranchContext((prev) => {
        const next = pickUrlBranchContext()
        const same =
          prev?.branchKey === next?.branchKey &&
          prev?.virtualBranch === next?.virtualBranch &&
          prev?.tokenKey === next?.tokenKey &&
          prev?.branchScope === next?.branchScope &&
          prev?.brokerPersonId === next?.brokerPersonId &&
          prev?.brokerCA === next?.brokerCA &&
          prev?.branchName === next?.branchName &&
          prev?.role === next?.role &&
          prev?.username === next?.username
        return same ? prev : next
      })
    }

    syncBranchContextFromUrl()
    window.addEventListener("locationchange", syncBranchContextFromUrl)
    return () => window.removeEventListener("locationchange", syncBranchContextFromUrl)
  }, [])

  useEffect(() => {
    const hasAnyContext =
      urlBranchContext?.branchScope ||
      urlBranchContext?.branchKey ||
      urlBranchContext?.virtualBranch ||
      urlBranchContext?.tokenKey ||
      urlBranchContext?.brokerPersonId ||
      urlBranchContext?.brokerCA ||
      urlBranchContext?.branchName

    if (!hasAnyContext) return

    try {
      const u = new URL(window.location.href)

      const currentBranchKey = u.searchParams.get("branchKey") || ""
      const currentVirtualBranch = u.searchParams.get("virtualBranch") || ""
      const currentTokenKey = u.searchParams.get("tokenKey") || ""
      const currentBranchScope = u.searchParams.get("branchScope") || ""
      const currentBrokerPersonId = u.searchParams.get("brokerPersonId") || ""
      const currentBrokerCA = u.searchParams.get("brokerCA") || ""
      const currentBranchName = u.searchParams.get("branchName") || ""

      const needsUpdate =
        currentBranchKey !== (urlBranchContext.branchKey || "") ||
        currentVirtualBranch !== (urlBranchContext.virtualBranch || "") ||
        currentTokenKey !== (urlBranchContext.tokenKey || "") ||
        currentBranchScope !== (urlBranchContext.branchScope || "broker") ||
        currentBrokerPersonId !== (urlBranchContext.brokerPersonId || "") ||
        currentBrokerCA !== (urlBranchContext.brokerCA || "") ||
        currentBranchName !== (urlBranchContext.branchName || "")

      if (!needsUpdate) return

      if (urlBranchContext.branchKey) u.searchParams.set("branchKey", urlBranchContext.branchKey)
      else u.searchParams.delete("branchKey")

      if (urlBranchContext.virtualBranch) u.searchParams.set("virtualBranch", urlBranchContext.virtualBranch)
      else u.searchParams.delete("virtualBranch")

      if (urlBranchContext.tokenKey) u.searchParams.set("tokenKey", urlBranchContext.tokenKey)
      else u.searchParams.delete("tokenKey")

      if (urlBranchContext.branchScope) u.searchParams.set("branchScope", urlBranchContext.branchScope)
      else u.searchParams.delete("branchScope")

      if (urlBranchContext.brokerPersonId) u.searchParams.set("brokerPersonId", urlBranchContext.brokerPersonId)
      else u.searchParams.delete("brokerPersonId")

      if (urlBranchContext.brokerCA) u.searchParams.set("brokerCA", urlBranchContext.brokerCA)
      else u.searchParams.delete("brokerCA")

      if (urlBranchContext.branchName) u.searchParams.set("branchName", urlBranchContext.branchName)
      else u.searchParams.delete("branchName")

      window.history.replaceState({}, "", u.toString())
      window.dispatchEvent(new Event("locationchange"))
    } catch (e) {
      console.error("Failed to backfill branch params into URL", e)
    }
  }, [urlBranchContext])

  const initialState = useMemo(
    () => ({
      mobile: {},
      property: {},
      applicants: [],
      income: [],
      assetsLiabilities: { assets: [], liabilities: [] },
      expenses: [],
      objectives: {},
      metadata: applyBranchMeta({ snapshotVersion: 0 }, urlBranchContext),
    }),
    [urlBranchContext]
  )

  const [crmData, setCrmData] = useState(initialState)
  const crmRef = useRef(initialState)

  useEffect(() => {
    crmRef.current = crmData
  }, [crmData])

  useEffect(() => {
    const hasBranchBits =
      urlBranchContext.branchKey ||
      urlBranchContext.virtualBranch ||
      urlBranchContext.tokenKey ||
      urlBranchContext.branchScope ||
      urlBranchContext.brokerPersonId ||
      urlBranchContext.brokerCA ||
      urlBranchContext.branchName

    if (!hasBranchBits) return

    setCrmData((prev) => {
      const nextMeta = applyBranchMeta(prev.metadata || {}, urlBranchContext)
      const same = JSON.stringify(prev.metadata || {}) === JSON.stringify(nextMeta)
      if (same) return prev

      const next = { ...prev, metadata: nextMeta }
      crmRef.current = next
      return next
    })
  }, [urlBranchContext])

  const mobileDigits = getMobileFromCrm(crmData)
  const hasMobile = isValidAUNumber(mobileDigits)

  const mercuryOpportunityId = crmData?.metadata?.mercuryOpportunityId || crmData?.metadata?.opportunityId || ""
  const hasMercuryIds = !!String(mercuryOpportunityId || "").trim()

  useEffect(() => {
    if (!hasMobile && currentSection !== "mobile") {
      setCurrentSection("mobile")
    }
  }, [hasMobile, currentSection])

  useEffect(() => {
    if (currentSection === "documents" && !hasMercuryIds) {
      setCurrentSection("review")
    }
  }, [currentSection, hasMercuryIds])

  useEffect(() => {
    if (!hasMobile && dialerOpen) setDialerOpen(false)
  }, [hasMobile, dialerOpen])

  useEffect(() => {
    if (!dialerOpen) return
    const onKeyDown = (e) => {
      if (e.key === "Escape") setDialerOpen(false)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [dialerOpen])

  const stableSig = (x) => JSON.stringify(x, (_k, v) => (v === undefined ? "" : v))

  const updateCrmData = useCallback((section, val) => {
    setCrmData((prev) => {
      const prevSection = prev?.[section]

      const nextSection =
        Array.isArray(val)
          ? val
          : typeof val === "object" && val !== null
            ? { ...(prevSection || {}), ...val }
            : val

      if (stableSig(prevSection) === stableSig(nextSection)) return prev

      const next = { ...prev, [section]: nextSection }
      crmRef.current = next
      setDirty(true)
      return next
    })
  }, [])

  const hydratedMobRef = useRef("")

  const hydrateSnapshotForMobile = useCallback(
    async (mobDigits10) => {
      const mob = digitsOnly(mobDigits10)
      if (!isValidAUNumber(mob)) return
      if (hydratedMobRef.current === mob) return
      if (dirty) return

      try {
        const url = `${SNAPSHOT_GET_BY_MOBILE_URL}?mobile=${encodeURIComponent(mob)}`
        const res = await fetchJsonLoose(url, { method: "GET" })

        if (res.ok && res.parsed?.success && res.parsed?.found && res.parsed?.item?.crmData) {
          const incoming = res.parsed.item.crmData
          const incomingVersion = Number(res.parsed.item.version || 0)

          const merged = {
            ...incoming,
            mobile: crmRef.current?.mobile || incoming.mobile || {},
            metadata: applyBranchMeta(
              {
                ...(incoming.metadata || {}),
                ...(crmRef.current?.metadata || {}),
                snapshotVersion: incomingVersion,
              },
              urlBranchContext
            ),
          }

          setCrmData(merged)
          crmRef.current = merged
          setDirty(false)
          setLastSaved(new Date())
        }

        hydratedMobRef.current = mob
      } catch (e) {
        console.error("Auto snapshot hydrate failed", e)
        hydratedMobRef.current = mob
      }
    },
    [dirty, urlBranchContext]
  )

  useEffect(() => {
    if (!hasMobile) return
    hydrateSnapshotForMobile(mobileDigits)
  }, [hasMobile, mobileDigits, hydrateSnapshotForMobile])

  useEffect(() => {
    if (!hasMobile) hydratedMobRef.current = ""
  }, [hasMobile])

  const getMercuryIds = useCallback(() => {
    const meta = crmRef.current?.metadata || {}
    return {
      opportunityId: meta.mercuryOpportunityId || meta.opportunityId || "",
      contactId: meta.mercuryContactId || meta.contactId || "",
    }
  }, [])

  const readMercuryNotes = useCallback(async () => {
    if (!APPEND_URL) return

    const { opportunityId, contactId } = getMercuryIds()

    setNotesError("")
    setNotesLoading(true)

    try {
      if (!opportunityId && !contactId) {
        setExistingMercuryNotes("")
        setNotesError("No Mercury IDs yet (submit first, or ensure mobile lookup saved IDs).")
        return
      }

      const qs = new URLSearchParams()
      if (opportunityId) qs.set("opportunityId", opportunityId)
      if (contactId) qs.set("contactId", contactId)

      const url = `${APPEND_URL}?${qs.toString()}`
      const res = await fetchJsonLoose(url, { method: "GET" })

      if (!res.ok || (res.statusCode && res.statusCode >= 400) || res.parsed?.success === false) {
        throw new Error(res.parsed?.message || `Read notepad failed (${res.statusCode || res.status || "?"})`)
      }

      const plain = String(res.parsed?.plainText || "").trim()
      setExistingMercuryNotes(plain)
      setNotesLastSync(new Date())
    } catch (e) {
      setExistingMercuryNotes("")
      setNotesError(e?.message || "Failed to load Mercury notes")
    } finally {
      setNotesLoading(false)
    }
  }, [getMercuryIds])

  const appendBrokerNotes = useCallback(
    async ({ brokerName }) => {
      if (!APPEND_URL) return { success: true, skipped: true, reason: "APPEND_URL not set" }

      const { opportunityId, contactId } = getMercuryIds()
      const draft = String(notesDraft || "").trim()

      if (!opportunityId && !contactId) {
        setNotesError("No Mercury IDs yet (submit first, or ensure mobile lookup saved IDs).")
        return { success: true, skipped: true, reason: "No Mercury IDs" }
      }
      if (!draft) return { success: true, skipped: true, reason: "No notes draft" }

      setNotesLoading(true)
      setNotesError("")
      try {
        const res = await fetchJsonLoose(APPEND_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            opportunityId,
            contactId,
            brokerName: brokerName || "Broker",
            entryTitle: "Broker Notes",
            appendText: draft,
          }),
        })

        if (!res.ok || (res.statusCode && res.statusCode >= 400) || res.parsed?.success === false) {
          throw new Error(res.parsed?.message || `Append notepad failed (${res.statusCode || res.status || "?"})`)
        }

        setNotesDraft("")

        const serverPlain = String(res.parsed?.serverPlainText || "").trim()
        const fallbackPlain = String(res.parsed?.plainText || "").trim()

        if (serverPlain || fallbackPlain) {
          setExistingMercuryNotes(serverPlain || fallbackPlain)
          setNotesLastSync(new Date())
        } else {
          await readMercuryNotes()
        }

        return res.parsed
      } catch (e) {
        setNotesError(e?.message || "Failed to append Mercury notes")
        return { success: false, message: e?.message || "Failed to append" }
      } finally {
        setNotesLoading(false)
      }
    },
    [getMercuryIds, notesDraft, readMercuryNotes]
  )

  const lastLoadedKeyRef = useRef("")
  useEffect(() => {
    const meta = crmData?.metadata || {}
    const opp = meta.mercuryOpportunityId || meta.opportunityId || ""
    const con = meta.mercuryContactId || meta.contactId || ""
    if (!opp && !con) return
    const key = `${opp}|${con}`
    if (key === lastLoadedKeyRef.current) return
    lastLoadedKeyRef.current = key
    readMercuryNotes()
  }, [
    crmData?.metadata?.mercuryOpportunityId,
    crmData?.metadata?.opportunityId,
    crmData?.metadata?.mercuryContactId,
    crmData?.metadata?.contactId,
    readMercuryNotes,
  ])

  const saveToMercury = useCallback(
    async (isFinal = false) => {
      try {
        setSaving(true)

        const latest = crmRef.current
        const meta = latest.metadata || {}
        const contactId = meta.mercuryContactId || null

        if (!isFinal) {
          const mob = getMobileFromCrm(latest)

          if (!isValidAUNumber(mob)) {
            return { success: true, skipped: true, reason: "No valid mobile yet" }
          }

          const res = await fetchJsonLoose(SNAPSHOT_UPSERT_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              mobile: mob,
              crmData: latest,
              version: Number(meta.snapshotVersion || 0),
            }),
          })

          if (!res.ok || res.parsed?.success === false) {
            if (res.parsed?.conflict) {
              alert("This file was updated elsewhere. Please refresh to load the latest data.")
            }
            throw new Error(res.parsed?.message || "Snapshot autosave failed")
          }

          const nextVersion = Number(res.parsed?.version || 0)
          setCrmData((prev) => {
            const nextMeta = applyBranchMeta(
              { ...(prev.metadata || {}), snapshotVersion: nextVersion },
              urlBranchContext
            )
            const next = { ...prev, metadata: nextMeta }
            crmRef.current = next
            return next
          })

          setDirty(false)
          setLastSaved(new Date())
          return { success: true }
        }

        const payload = buildSendPayload(latest)
        if (contactId) payload.existingContactId = contactId

        const finalPayload = {
          ...payload,
          ...(meta?.branchKey ? { branchKey: meta.branchKey } : {}),
          ...(meta?.virtualBranch ? { virtualBranch: meta.virtualBranch } : {}),
          ...(meta?.tokenKey ? { tokenKey: meta.tokenKey } : {}),
          ...(meta?.branchScope ? { branchScope: meta.branchScope } : {}),
          ...(meta?.brokerPersonId ? { brokerPersonId: meta.brokerPersonId } : {}),
          ...(meta?.brokerCA ? { brokerCA: meta.brokerCA } : {}),
          ...(meta?.branchName ? { branchName: meta.branchName } : {}),
        }

        const resp = await fetch(SEND_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(finalPayload),
        })

        const json = await resp.json()

        if (json?.success) {
          const nextContactId = json?.contactId || json?.mercuryContactId || json?.existingContactId || ""
          const nextOppId = json?.opportunityId || json?.mercuryOpportunityId || ""

          if (nextContactId || nextOppId) {
            setCrmData((prev) => {
              const nextMeta = applyBranchMeta(
                {
                  ...(prev.metadata || {}),
                  ...(nextContactId ? { mercuryContactId: nextContactId } : {}),
                  ...(nextOppId ? { mercuryOpportunityId: nextOppId } : {}),
                },
                urlBranchContext
              )
              const next = { ...prev, metadata: nextMeta }
              crmRef.current = next
              return next
            })
          }

          try {
            await readMercuryNotes()
          } catch {}
        }

        return json
      } catch (err) {
        console.error("SAVE ERROR", err)
        return { success: false, message: err?.message || "Unknown error" }
      } finally {
        setSaving(false)
      }
    },
    [readMercuryNotes, urlBranchContext]
  )

  useEffect(() => {
    if (!dirty) return
    const t = setInterval(() => saveToMercury(false), 15000)
    return () => clearInterval(t)
  }, [dirty, saveToMercury])

  const sections = [
    { id: "mobile", label: "Mobile", icon: StepIcons.mobile },
    { id: "property", label: "Property", icon: StepIcons.property },
    { id: "applicants", label: "Applicants", icon: StepIcons.applicants },
    { id: "income", label: "Income", icon: StepIcons.income },
    { id: "assets", label: "Assets & Liabilities", icon: StepIcons.assets },
    { id: "expenses", label: "Expenses", icon: StepIcons.expenses },
    { id: "objectives", label: "Needs & Objectives", icon: StepIcons.objectives },
    { id: "capacity", label: "Choose Your Loan", icon: StepIcons.capacity },
    { id: "review", label: "Review & Submit", icon: StepIcons.review },
    { id: "documents", label: "Documents", icon: StepIcons.documents, requiresMercury: true },
  ]

  const visibleSections = (() => {
    if (!hasMobile) {
      return sections.filter((s) => s.id === "mobile")
    }
    return sections.filter((s) => {
      if (!s.requiresMercury) return true
      return hasMercuryIds
    })
  })()

  const handleNextFrom = async (id) => {
    const latest = crmRef.current
    const check = validateSection(id, latest)
    if (!check.valid) return alert(check.message)

    if (id === "mobile") {
      setCrmData((prev) => {
        const next = {
          ...prev,
          metadata: applyBranchMeta(
            {
              ...(prev.metadata || {}),
              snapshotVersion: Number(prev?.metadata?.snapshotVersion || 0),
            },
            urlBranchContext
          ),
        }
        crmRef.current = next
        return next
      })

      const i = sections.findIndex((s) => s.id === id)
      setCurrentSection(sections[i + 1]?.id)
      return
    }

    await saveToMercury(false)

    const i = sections.findIndex((s) => s.id === id)
    setCurrentSection(sections[i + 1]?.id)
  }

  const handleBackFrom = (id) => {
    const i = sections.findIndex((s) => s.id === id)
    setCurrentSection(sections[i - 1]?.id)
  }

  const safeNavigate = useCallback(
    async (nextId) => {
      if (!hasMobile && nextId !== "mobile") {
        alert("Enter a valid mobile number first (this links the file to Mercury).")
        return
      }

      if (nextId === "documents" && !hasMercuryIds) {
        alert("Documents are available after Review & Submit (Mercury IDs required).")
        return
      }

      try {
        if (currentSection !== "mobile") {
          await saveToMercury(false)
        }
      } catch (e) {
        console.error("NAV SAVE ERROR", e)
      } finally {
        setCurrentSection(nextId)
      }
    },
    [currentSection, saveToMercury, hasMobile, hasMercuryIds]
  )

  const progressSections = sections.filter((s) => s.id !== "documents")
  const currentIndex = progressSections.findIndex((s) => s.id === currentSection)
  const reviewIndex = progressSections.findIndex((s) => s.id === "review")
  const idxForProgress = currentSection === "documents" ? reviewIndex : currentIndex
  const progress = Math.round((Math.max(0, idxForProgress) / (progressSections.length - 1)) * 100)

  const consentCrmRef =
    crmData?.metadata?.crmRef || crmData?.metadata?.leadRef || crmData?.metadata?.ref || crmData?.metadata?.recordId || ""

  const consentOpportunityId = crmData?.metadata?.mercuryOpportunityId || crmData?.metadata?.opportunityId || ""

  const brokerNameForNotes =
    crmData?.metadata?.brokerName ||
    crmData?.metadata?.branchName ||
    crmData?.metadata?.userName ||
    crmData?.metadata?.username ||
    "Broker"

  const finalSubmitAndOpenDocuments = useCallback(async () => {
    const res = await saveToMercury(true)
    if (res?.success) {
      setCurrentSection("documents")
    }
    return res
  }, [saveToMercury])

  const currentLead = useMemo(() => {
    const a1 = (crmData?.applicants || [])[0] || {}
    const a2 = (crmData?.applicants || [])[1] || {}

    const meta = crmData?.metadata || {}
    const contactId = meta.mercuryContactId || meta.contactId || ""
    const opportunityId = meta.mercuryOpportunityId || meta.opportunityId || ""
    const mob = getMobileFromCrm(crmData)

    return {
      mobile: mob,
      firstName: a1.firstName || "",
      lastName: a1.lastName || "",
      email: a1.email || "",
      coFirstName: a2.firstName || "",
      coLastName: a2.lastName || "",
      contactId,
      opportunityId,
      applicationType: crmData?.property?.applicationType || "",
      step: currentSection,
    }
  }, [crmData, currentSection])

  const screenPopToMobile = useCallback(
    (digits10) => {
      const m = digitsOnly(digits10)
      if (!m) return

      const meta = crmRef.current?.metadata || {}
      const opportunityId = meta?.mercuryOpportunityId || meta?.opportunityId || ""
      const contactId = meta?.mercuryContactId || meta?.contactId || ""
      const crmRefVal = meta?.crmRef || meta?.leadRef || meta?.ref || ""

      try {
        const u = new URL(window.location.href)
        u.searchParams.set("mobile", m)

        if (urlBranchContext?.branchKey) u.searchParams.set("branchKey", urlBranchContext.branchKey)
        if (urlBranchContext?.virtualBranch) u.searchParams.set("virtualBranch", urlBranchContext.virtualBranch)
        if (urlBranchContext?.tokenKey) u.searchParams.set("tokenKey", urlBranchContext.tokenKey)
        if (urlBranchContext?.branchScope) u.searchParams.set("branchScope", urlBranchContext.branchScope)
        if (urlBranchContext?.brokerPersonId) u.searchParams.set("brokerPersonId", urlBranchContext.brokerPersonId)
        if (urlBranchContext?.brokerCA) u.searchParams.set("brokerCA", urlBranchContext.brokerCA)
        if (urlBranchContext?.branchName) u.searchParams.set("branchName", urlBranchContext.branchName)

        if (opportunityId) u.searchParams.set("opportunityId", opportunityId)
        if (contactId) u.searchParams.set("contactId", contactId)
        if (crmRefVal) u.searchParams.set("crmRef", crmRefVal)

        window.history.pushState({}, "", u.toString())
      } catch (e) {
        const qs = new URLSearchParams()
        qs.set("mobile", m)

        if (urlBranchContext?.branchKey) qs.set("branchKey", urlBranchContext.branchKey)
        if (urlBranchContext?.virtualBranch) qs.set("virtualBranch", urlBranchContext.virtualBranch)
        if (urlBranchContext?.tokenKey) qs.set("tokenKey", urlBranchContext.tokenKey)
        if (urlBranchContext?.branchScope) qs.set("branchScope", urlBranchContext.branchScope)
        if (urlBranchContext?.brokerPersonId) qs.set("brokerPersonId", urlBranchContext.brokerPersonId)
        if (urlBranchContext?.brokerCA) qs.set("brokerCA", urlBranchContext.brokerCA)
        if (urlBranchContext?.branchName) qs.set("branchName", urlBranchContext.branchName)

        if (opportunityId) qs.set("opportunityId", opportunityId)
        if (contactId) qs.set("contactId", contactId)
        if (crmRefVal) qs.set("crmRef", crmRefVal)

        window.location.href = `?${qs.toString()}`
      }
    },
    [urlBranchContext]
  )

  const formatAU = (digits) => {
    const d = digitsOnly(digits)
    if (d.length !== 10) return digits || ""
    return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`
  }

  const leadName = useMemo(() => {
    const fn = String(currentLead?.firstName || "").trim()
    const ln = String(currentLead?.lastName || "").trim()
    const full = `${fn} ${ln}`.trim()
    return full || "Unknown client"
  }, [currentLead])

  const initials = useMemo(() => {
    const fn = String(currentLead?.firstName || "").trim()
    const ln = String(currentLead?.lastName || "").trim()
    const a = fn ? fn[0].toUpperCase() : ""
    const b = ln ? ln[0].toUpperCase() : ""
    const out = `${a}${b}` || "•"
    return out.slice(0, 2)
  }, [currentLead])

  const DIALER_API_BASE = process.env.REACT_APP_DIALER_API_BASE || ""
  const dialerConfigured = !!String(DIALER_API_BASE || "").trim()

  const fabSize = 56

  const fabStyle = {
    position: "fixed",
    right: 18,
    bottom: 18,
    zIndex: 9999,
    width: fabSize,
    height: fabSize,
    borderRadius: 999,
    border: "1px solid rgba(20,20,20,0.10)",
    background: "#4EDCF6",
    color: "#141414",
    boxShadow: "0 18px 45px rgba(0,0,0,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: hasMobile ? "pointer" : "not-allowed",
    opacity: hasMobile ? 1 : 0.55,
    userSelect: "none",
    transform: dialerOpen ? "scale(0.96)" : "scale(1)",
    transition: "transform 180ms ease, box-shadow 180ms ease, opacity 180ms ease",
  }

  const overlayStyle = {
    position: "fixed",
    inset: 0,
    zIndex: 9998,
    background: "rgba(20,20,20,0.22)",
    backdropFilter: "blur(6px)",
  }

  const drawerStyle = {
    position: "fixed",
    right: 16,
    bottom: 16,
    zIndex: 10000,
    width: "min(420px, calc(100vw - 32px))",
    height: "min(74vh, 680px)",
    borderRadius: 22,
    background: "#fff",
    border: "1px solid rgba(20,20,20,0.10)",
    boxShadow: "0 30px 80px rgba(0,0,0,0.24)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  }

  const drawerHeaderStyle = {
    padding: "12px 14px",
    borderBottom: "1px solid rgba(20,20,20,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    background: "linear-gradient(180deg, rgba(78,220,246,0.14) 0%, rgba(255,255,255,1) 72%)",
  }

  const statusPillStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(20,20,20,0.10)",
    background: "rgba(255,255,255,0.80)",
    fontSize: 12,
    fontWeight: 650,
    color: "rgba(20,20,20,0.75)",
  }

  const dotStyle = (ok) => ({
    width: 8,
    height: 8,
    borderRadius: 999,
    background: ok ? "#22c55e" : "rgba(20,20,20,0.35)",
    boxShadow: ok ? "0 0 0 4px rgba(34,197,94,0.14)" : "0 0 0 4px rgba(20,20,20,0.06)",
  })

  const iconBtnStyle = {
    width: 38,
    height: 38,
    borderRadius: 12,
    border: "1px solid rgba(20,20,20,0.10)",
    background: "#fff",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  }

  const leadCardStyle = {
    border: "1px solid rgba(20,20,20,0.08)",
    background: "linear-gradient(180deg, rgba(78,220,246,0.08) 0%, rgba(255,255,255,1) 60%)",
    borderRadius: 18,
    padding: 12,
    display: "flex",
    gap: 12,
    alignItems: "center",
  }

  const avatarStyle = {
    width: 44,
    height: 44,
    borderRadius: 16,
    background: "#4EDCF6",
    color: "#141414",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 850,
    letterSpacing: "0.5px",
    border: "1px solid rgba(20,20,20,0.10)",
    boxShadow: "0 12px 30px rgba(0,0,0,0.10)",
    flexShrink: 0,
  }

  const quiet = { color: "rgba(20,20,20,0.60)", fontWeight: 600 }

  const chipStyle = {
    fontSize: 12,
    fontWeight: 650,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(20,20,20,0.10)",
    background: "#fff",
    color: "rgba(20,20,20,0.75)",
  }

  const emptyStateStyle = {
    marginTop: 10,
    border: "1px solid rgba(20,20,20,0.08)",
    borderRadius: 16,
    padding: 12,
    background: "rgba(20,20,20,0.02)",
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
  }

  const smallIconWrap = {
    width: 28,
    height: 28,
    borderRadius: 10,
    border: "1px solid rgba(20,20,20,0.10)",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }

  const showSharedNav = !["mobile", "review", "documents"].includes(currentSection)

  const sharedNav = showSharedNav ? (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        marginTop: 20,
        paddingTop: 8,
        flexWrap: "wrap",
      }}
    >
      <button
        type="button"
        onClick={() => handleBackFrom(currentSection)}
        style={{
          minWidth: 140,
          height: 52,
          padding: "0 18px",
          borderRadius: 14,
          border: "1px solid rgba(20,20,20,0.14)",
          background: "#ffffff",
          color: "#141414",
          fontSize: 15,
          fontWeight: 650,
          cursor: "pointer",
          boxShadow: "0 6px 18px rgba(20,20,20,0.05)",
        }}
      >
        ← Back
      </button>

      <button
        type="button"
        onClick={() => handleNextFrom(currentSection)}
        style={{
          minWidth: 140,
          height: 52,
          padding: "0 18px",
          borderRadius: 14,
          border: "1px solid #141414",
          background: "#141414",
          color: "#ffffff",
          fontSize: 15,
          fontWeight: 700,
          cursor: "pointer",
          boxShadow: "0 10px 24px rgba(20,20,20,0.14)",
        }}
      >
        Next →
      </button>
    </div>
  ) : null

  return (
    <div className={styles.crmShell}>
      <CRMTopNav progress={progress} saving={saving} dirty={dirty} lastSaved={lastSaved} />

      <div className={styles.crmContainer}>
        <div className={styles.sidebar}>
          <ul>
            {visibleSections.map((s) => (
              <li key={s.id} className={currentSection === s.id ? styles.active : ""} onClick={() => safeNavigate(s.id)}>
                <span className={styles.stepIcon} aria-hidden="true">
                  {s.icon}
                </span>
                <span className={styles.stepLabel}>{s.label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.main}>
          <div className={styles.sectionContent}>
            {currentSection === "mobile" && (
              <MobileStep
                crmData={crmData}
                updateCrmData={updateCrmData}
                branchContext={urlBranchContext}
                next={() => handleNextFrom("mobile")}
              />
            )}

            {currentSection === "property" && (
              <PropertyStep
                data={crmData.property}
                assetsLiabilities={crmData.assetsLiabilities}
                updateSection={updateCrmData}
              />
            )}

            {currentSection === "applicants" && (
              <ApplicantStep
                data={crmData.applicants}
                updateCrmData={updateCrmData}
                crmRef={consentCrmRef}
                opportunityId={consentOpportunityId}
              />
            )}

            {currentSection === "income" && (
              <IncomeExpenses
                data={crmData.income}
                updateCrmData={updateCrmData}
              />
            )}

            {currentSection === "assets" && (
              <AssetsLiabilities
                data={crmData.assetsLiabilities}
                updateCrmData={(v) => updateCrmData("assetsLiabilities", v)}
                property={crmData.property}
              />
            )}

            {currentSection === "expenses" && (
              <ExpensesStep
                data={crmData.expenses}
                updateCrmData={(v) => updateCrmData("expenses", v)}
              />
            )}

            {currentSection === "objectives" && (
              <NeedsObjectives
                data={crmData.objectives}
                updateCrmData={(v) => updateCrmData("objectives", v)}
              />
            )}

            {currentSection === "capacity" && (
              <BorrowingCapacityStep
                crmData={crmData}
              />
            )}

            {currentSection === "review" && (
              <ReviewSubmit crmData={crmData} saveToMercury={finalSubmitAndOpenDocuments} onBack={() => handleBackFrom("review")} />
            )}

            {currentSection === "documents" && (
              <DocumentsTab
                mercuryOpportunityId={mercuryOpportunityId}
                clientMobile={mobileDigits}
                incomeRows={crmData?.income || []}
              />
            )}

            {sharedNav}
          </div>
        </div>

        <div className={styles.rightSidebar}>
          <SuggestedQuestions step={currentSection} applicationType={crmData?.property?.applicationType || ""} />

          <div className={styles.brokerNotes}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <h4 style={{ margin: 0 }}>📝 Broker Notes</h4>
              <button
                type="button"
                onClick={readMercuryNotes}
                disabled={notesLoading}
                style={{
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid rgba(20,20,20,0.15)",
                  background: "#fff",
                  cursor: notesLoading ? "not-allowed" : "pointer",
                  fontWeight: 650,
                }}
                title="Refresh from Mercury"
              >
                {notesLoading ? "Loading…" : "Refresh"}
              </button>
            </div>

            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
                Existing (from Mercury){notesLastSync ? ` • synced ${notesLastSync.toLocaleString("en-AU")}` : ""}
              </div>

              <textarea
                value={existingMercuryNotes || ""}
                readOnly
                placeholder={
                  notesError
                    ? notesError
                    : "No Mercury notes yet (submit first, or refresh after IDs exist)."
                }
                style={{
                  width: "100%",
                  minHeight: 140,
                  resize: "vertical",
                  background: "rgba(20,20,20,0.03)",
                }}
              />

              {notesError ? (
                <div style={{ marginTop: 8, fontSize: 12, color: "#D11A2A" }}>
                  {notesError}
                  <div style={{ marginTop: 6, opacity: 0.8 }}>
                    Notes service: <span style={{ fontFamily: "monospace" }}>{APPEND_URL}</span>
                  </div>
                </div>
              ) : null}
            </div>

            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
                Add new note (will append to Mercury)
              </div>
              <textarea
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                placeholder="Type your note…"
                style={{ width: "100%", minHeight: 110, resize: "vertical" }}
              />

              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => appendBrokerNotes({ brokerName: brokerNameForNotes })}
                  disabled={notesLoading || !String(notesDraft || "").trim()}
                  style={{
                    width: "100%",
                    padding: "12px 12px",
                    borderRadius: 12,
                    border: "none",
                    background: "#4EDCF6",
                    color: "#141414",
                    fontWeight: 750,
                    cursor: notesLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {notesLoading ? "Saving…" : "Add to Mercury"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        style={fabStyle}
        onClick={() => {
          if (!hasMobile) return
          setDialerOpen(true)
        }}
        title={!hasMobile ? "Enter a valid mobile first" : "Open dialler"}
        aria-label="Open dialler"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.86.31 1.7.57 2.5a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.58-1.09a2 2 0 0 1 2.11-.45c.8.26 1.64.45 2.5.57A2 2 0 0 1 22 16.92z" />
        </svg>
      </div>

      {dialerOpen ? (
        <>
          <div style={overlayStyle} onClick={() => setDialerOpen(false)} />

          <div style={drawerStyle} role="dialog" aria-modal="true" aria-label="Dialler drawer">
            <div style={drawerHeaderStyle}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14, lineHeight: "16px" }}>Dialler</div>
                <div style={{ fontSize: 12, opacity: 0.65, marginTop: 2 }}>CallHippo</div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={statusPillStyle} title="Status">
                  <span style={dotStyle(true)} />
                  <span>Idle</span>
                </div>

                <button type="button" onClick={() => setDialerOpen(false)} style={iconBtnStyle} title="Close" aria-label="Close dialler">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M18 6 6 18" />
                    <path d="M6 6 18 18" />
                  </svg>
                </button>
              </div>
            </div>

            <div style={{ padding: 14, overflow: "auto" }}>
              <div style={leadCardStyle}>
                <div style={avatarStyle} aria-hidden="true">
                  {initials}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 780,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {leadName}
                  </div>
                  <div style={{ marginTop: 2, fontSize: 12, ...quiet }}>
                    {formatAU(currentLead?.mobile) || "No mobile"}
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                    {currentLead?.applicationType ? (
                      <div style={chipStyle}>{String(currentLead.applicationType).toUpperCase()}</div>
                    ) : null}
                    {currentLead?.opportunityId ? <div style={chipStyle}>Opp ✓</div> : <div style={chipStyle}>Opp —</div>}
                    {currentLead?.contactId ? <div style={chipStyle}>Contact ✓</div> : <div style={chipStyle}>Contact —</div>}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const m = digitsOnly(currentLead?.mobile || "")
                    if (m) screenPopToMobile(m)
                  }}
                  style={{
                    ...iconBtnStyle,
                    borderRadius: 14,
                    width: 44,
                    height: 44,
                    opacity: currentLead?.mobile ? 1 : 0.5,
                    cursor: currentLead?.mobile ? "pointer" : "not-allowed",
                  }}
                  title="Open lead"
                  aria-label="Open lead"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M14 3h7v7" />
                    <path d="M10 14 21 3" />
                    <path d="M21 14v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h6" />
                  </svg>
                </button>
              </div>

              {!dialerConfigured ? (
                <div style={emptyStateStyle}>
                  <div style={smallIconWrap} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 9v4" />
                      <path d="M12 17h.01" />
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 750, fontSize: 13 }}>Dialler not configured</div>
                    <div style={{ marginTop: 4, fontSize: 12, color: "rgba(20,20,20,0.65)" }}>
                      Set <span style={{ fontFamily: "monospace" }}>REACT_APP_DIALER_API_BASE</span> to show CallHippo actions.
                    </div>
                  </div>
                </div>
              ) : null}

              {!currentLead?.mobile ? (
                <div style={emptyStateStyle}>
                  <div style={smallIconWrap} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 750, fontSize: 13 }}>No lead selected</div>
                    <div style={{ marginTop: 4, fontSize: 12, color: "rgba(20,20,20,0.65)" }}>
                      Enter a valid mobile on the Mobile step to enable calling.
                    </div>
                  </div>
                </div>
              ) : null}

              <div style={{ marginTop: 12 }}>
                <DialerPanel
                  lead={currentLead}
                  disabled={!hasMobile}
                  onScreenPop={(m) => screenPopToMobile(m)}
                  mode="drawer"
                />
              </div>
            </div>
          </div>
        </>
      ) : null}

      <div
        style={{
          width: "100%",
          padding: "12px 16px",
          borderTop: "1px solid rgba(20,20,20,0.08)",
          background: "#fff",
          color: "rgba(20,20,20,0.7)",
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        © {new Date().getFullYear()} Mortgage Matrix • Broker CRM
      </div>
    </div>
  )
}